<template>
  <div
    :class="{ fullscreen: fullscreen }"
    class="tinymce-container"
    :style="{ width: containerWidth }"
  >
    <textarea :id="id" class="tinymce-textarea" rows="5" />
    <div class="editor-custom-btn-container">
      <UploadPan
        ref="upload"
        v-model="fileList"
        :form="form"
        class="editor-upload-btn"
        :model="models"
        @on-close="handleCloseUpload"
        @on-update="handleOnUpdate"
        :accept="accept"
        color="#1890ff"
      />
    </div>
  </div>
</template>

<script>
/**
 * docs:
 * https://panjiachen.github.io/vue-element-admin-site/feature/component/rich-editor.html#tinymce
 */
import UploadPan from '../../components/Upload'
import plugins from './plugins'
import toolbar from './toolbar'
import load from './dynamicLoadScript'

// // why use this cdn, detail see https://github.com/PanJiaChen/tinymce-all-in-one
// const tinymceCDN =
//   'https://cdnjs.cloudflare.com/ajax/libs/tinymce/4.9.3/tinymce.min.js'

const tinymceCDN = window.location.origin + '/static/tinymce/tinymce.min.js'

export default {
  name: 'Tinymce',
  components: { UploadPan },
  props: {
    id: {
      type: String,
      default: function () {
        return (
          'vue-tinymce-' +
          +new Date() +
          ((Math.random() * 1000).toFixed(0) + '')
        )
      },
    },
    value: {
      type: String,
      default: '',
    },
    toolbar: {
      type: Array,
      required: false,
      default() {
        return []
      },
    },
    menubar: {
      type: String,
      default: 'file edit insert view format table',
    },
    height: {
      type: [Number, String],
      required: false,
      default: 260,
    },
    width: {
      type: [Number, String],
      required: false,
      default: 'auto',
    },
    accept: {
      type: String,
      default: '',
    },
    form: {
      type: Object,
      default: () => {},
    },
    model: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      fileList: [],
      models: `hrmarket/${new Date().getFullYear()}${
        new Date().getMonth() + 1
      }`,
      hasChange: false,
      hasInit: false,
      tinymceId: this.id,
      fullscreen: false,
    }
  },
  computed: {
    containerWidth() {
      const width = this.width
      if (/^[\d]+(\.[\d]+)?$/.test(width)) {
        // matches `100`, `'100'`
        return `${width}px`
      }
      return width
    },
  },
  watch: {
    value(val) {
      if (!this.hasChange && this.hasInit) {
        this.$nextTick(() => {
          window.tinymce.get(this.tinymceId).setContent(val || '')
        })
      }
    },
  },
  mounted() {
    this.init()
  },
  activated() {
    if (window.tinymce) {
      this.initTinymce()
    }
  },
  deactivated() {
    this.destroyTinymce()
  },
  destroyed() {
    this.destroyTinymce()
  },
  methods: {
    init() {
      console.log('init methods')
      // dynamic load tinymce from cdn
      load(tinymceCDN, (err) => {
        if (err) {
          this.$message.error(err.message)
          return
        }
        window.tinymce.baseURL = window.location.origin + '/static/tinymce'
        this.initTinymce()
      })
    },
    initTinymce() {
      const _this = this
      window.tinymce.init({
        language_url: '/static/tinymce/langs/zh_CN.js',
        selector: `#${this.tinymceId}`,
        language: 'zh_CN',
        height: this.height,
        body_class: 'panel-body ',
        object_resizing: false,
        toolbar: this.toolbar.length > 0 ? this.toolbar : toolbar,
        menubar: this.menubar,
        plugins: plugins,
        fontsize_formats: '11px 12px 14px 16px 18px 24px 36px 48px',
        end_container_on_empty_block: true,
        powerpaste_word_import: 'clean',
        code_dialog_height: 450,
        code_dialog_width: 1000,
        advlist_bullet_styles: 'square',
        advlist_number_styles: 'default',
        imagetools_cors_hosts: ['www.tinymce.com', 'codepen.io'],
        default_link_target: '_blank',
        link_title: false,
        valid_children: '+div[style],+div[meta]',
        nonbreaking_force_tab: true, // inserting nonbreaking space &nbsp; need Nonbreaking Space Plugin
        init_instance_callback: (editor) => {
          if (_this.value) {
            editor.setContent(_this.value)
            editor.selection.select(editor.getBody(), true)
            editor.selection.collapse(false)
          }
          _this.hasInit = true
          editor.on('NodeChange Change KeyUp SetContent', () => {
            this.hasChange = true
            this.$emit('input', editor.getContent())
          })
        },
        setup(editor) {
          editor.on('FullscreenStateChanged', (e) => {
            _this.fullscreen = e.state
          })
        },
        convert_urls: false,
      })
    },
    destroyTinymce() {
      const tinymce = window.tinymce.get(this.tinymceId)
      if (this.fullscreen) {
        tinymce.execCommand('mceFullScreen')
      }

      if (tinymce) {
        tinymce.destroy()
      }
    },
    setContent(value) {
      window.tinymce.get(this.tinymceId).setContent(value)
    },
    getContent() {
      window.tinymce.get(this.tinymceId).getContent()
    },
    handleCloseUpload() {
      this.fileList = []
    },
    handleOnUpdate(obj) {
      this.fileList.forEach((v) => {
        let content = ''
        const url = `https://${this.form.bucket}.${this.form.region}.aliyuncs.com/${v.uploadName}`
        const { type } = v.raw
        if (type.indexOf('image') !== -1) {
          content = `<img class="wscnph" src="${url}" >`
        } else if (type.indexOf('video') !== -1) {
          content = `<video controls="controls" width="300" height="150"><source src="${url}" /></video>`
        }
        window.tinymce.get(this.tinymceId).insertContent(content)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.tinymce-container {
  position: relative;
  line-height: normal;
}

.tinymce-container {
  ::v-deep {
    .mce-fullscreen {
      z-index: 10000;
    }
  }
}

.tinymce-textarea {
  visibility: hidden;
  z-index: -1;
}

.editor-custom-btn-container {
  position: absolute;
  right: 4px;
  top: 4px;
  /*z-index: 2005;*/
}

.fullscreen .editor-custom-btn-container {
  z-index: 10000;
  position: fixed;
}

.editor-upload-btn {
  display: inline-block;
}
</style>
